// apps/web/src/pages/PracticePage.tsx
// 练习页 — 顺序/随机练习核心流程

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import QuestionRenderer from "@components/practice/QuestionRenderer";
import { startPracticeApi, completeSessionApi, syncPracticeAnswersApi, getQuestionPracticeStatsApi } from "@api/practice";
import { addFavoriteApi, createNoteApi } from "@api/userData";
import { clearBankPracticeRecordsApi, getBankPracticeRecordsApi, getBankQuestionTypeStatsApi } from "@api/banks";
import type {
  BankPracticeRecordDTO,
  QuestionDTO,
  QuestionPracticeStatsDTO,
  StartPracticeResponse,
  SubmitAnswerResponse,
  SyncPracticeAnswerItem,
} from "@examfast/shared-types";

type PracticeStatus = "loading" | "active" | "result" | "completed";
type AnswerRecord = { isCorrect: boolean; userAnswer: string | string[] | boolean };

type FontSizeSetting = "small" | "standard" | "large" | "xlarge" | "xxlarge" | "max";
type OptionSpacingSetting = "standard" | "wide" | "xwide";
type MockExamConfig = {
  single_choice: { count: number; score: number };
  multiple_choice: { count: number; score: number };
  true_false: { count: number; score: number };
  fill_blank: { count: number; score: number };
  durationMinutes: number;
  passScore: number;
  showAnswerAfterSubmit: boolean;
  randomOrder: boolean;
  wrongFirst: boolean;
  unansweredFirst: boolean;
};

interface PracticeSettings {
  optionDisplay: "normal";
  showPrevNext: boolean;
  autoNextOnCorrect: boolean;
  randomOrder: boolean;
  answerSound: boolean;
  readExplanation: boolean;
  readAnswer: boolean;
  randomQuestionCount: number;
  wrongRepeatThreshold: number;
  fontSize: FontSizeSetting;
  optionSpacing: OptionSpacingSetting;
}

const DEFAULT_PRACTICE_SETTINGS: PracticeSettings = {
  optionDisplay: "normal",
  showPrevNext: false,
  autoNextOnCorrect: true,
  randomOrder: false,
  answerSound: false,
  readExplanation: false,
  readAnswer: false,
  randomQuestionCount: 50,
  wrongRepeatThreshold: 3,
  fontSize: "standard",
  optionSpacing: "standard",
};

const SETTINGS_STORAGE_KEY = "examfast.practice.settings";
const ANSWER_SYNC_BATCH_SIZE = 5;

const DEFAULT_MOCK_EXAM_CONFIG: MockExamConfig = {
  single_choice: { count: 10, score: 1 },
  multiple_choice: { count: 10, score: 1 },
  true_false: { count: 10, score: 1 },
  fill_blank: { count: 5, score: 1 },
  durationMinutes: 40,
  passScore: 45,
  showAnswerAfterSubmit: true,
  randomOrder: false,
  wrongFirst: false,
  unansweredFirst: false,
};

function loadPracticeSettings(): PracticeSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? { ...DEFAULT_PRACTICE_SETTINGS, ...JSON.parse(raw) } : DEFAULT_PRACTICE_SETTINGS;
  } catch {
    return DEFAULT_PRACTICE_SETTINGS;
  }
}

function savePracticeSettings(settings: PracticeSettings) {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

function parsePracticeTypes(raw: string | null): QuestionDTO["type"][] | undefined {
  if (!raw) return undefined;
  const values = raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean) as QuestionDTO["type"][];
  return values.length > 0 ? values : undefined;
}

export default function PracticePage() {
  const { bankId, mode } = useParams<{ bankId: string; mode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedTypes = parsePracticeTypes(searchParams.get("types"));
  const trueFalseAnswerParam = searchParams.get("tfAnswer");
  const difficultyBucketParam = searchParams.get("difficultyBucket");
  const queryClient = useQueryClient();

  // 核心状态
  const [status, setStatus] = useState<PracticeStatus>("loading");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<QuestionDTO | null>(null);
  const [questions, setQuestions] = useState<QuestionDTO[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState<SubmitAnswerResponse | null>(null);
  const [score, setScore] = useState<{ score: number; correct_count: number; total_count: number } | null>(null);
  const [answerStats, setAnswerStats] = useState({ correct: 0, wrong: 0 });
  const [answerRecords, setAnswerRecords] = useState<Record<string, AnswerRecord>>({});
  const [optimisticQuestionStats, setOptimisticQuestionStats] = useState<Record<string, QuestionPracticeStatsDTO>>({});
  const [settings, setSettings] = useState<PracticeSettings>(() => loadPracticeSettings());
  const [mockConfig, setMockConfig] = useState<MockExamConfig>(DEFAULT_MOCK_EXAM_CONFIG);
  const [mockStarted, setMockStarted] = useState(false);
  const [answerInputResetKey, setAnswerInputResetKey] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);

  // 进度计数
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  // 计时
  const questionStartTimeRef = useRef<number>(Date.now());
  const pendingAnswersRef = useRef<SyncPracticeAnswerItem[]>([]);
  const flushInFlightRef = useRef<Promise<void> | null>(null);

  const { data: typeStats } = useQuery({
    queryKey: ["bank-type-stats", bankId],
    queryFn: () => getBankQuestionTypeStatsApi(bankId!),
    enabled: !!bankId && mode === "mock" && !mockStarted,
  });

  const { data: serverQuestionStats } = useQuery({
    queryKey: ["question-practice-stats", currentQuestion?.id],
    queryFn: () => getQuestionPracticeStatsApi(currentQuestion!.id),
    enabled: !!currentQuestion,
  });

  const currentQuestionStats = mergeQuestionStats(
    serverQuestionStats,
    currentQuestion ? optimisticQuestionStats[currentQuestion.id] : undefined
  );

  // ========== 1. 开始练习 ==========

  const startMutation = useMutation({
    mutationFn: async () => {
      const practice = await startPracticeApi({
        bank_id: bankId!,
        mode: mode!,
        settings: {
          question_count: mode === "random" ? settings.randomQuestionCount : undefined,
          types: mode === "type" ? selectedTypes : undefined,
          true_false_answer:
            mode === "type" && trueFalseAnswerParam !== null
              ? trueFalseAnswerParam === "true"
              : undefined,
          difficulty_bucket:
            mode === "type" && (difficultyBucketParam === "easy" || difficultyBucketParam === "medium" || difficultyBucketParam === "hard")
              ? difficultyBucketParam
              : undefined,
          random_order: mode === "mock" ? mockConfig.randomOrder : settings.randomOrder,
          start_question_id: searchParams.get("questionId") ?? undefined,
          wrong_repeat_threshold: settings.wrongRepeatThreshold,
          time_limit_seconds: mode === "mock" ? mockConfig.durationMinutes * 60 : undefined,
          type_counts: mode === "mock" ? buildMockTypeCounts(mockConfig) : undefined,
          score_by_type: mode === "mock" ? buildMockScoreByType(mockConfig) : undefined,
          show_answer_after_submit: mode === "mock" ? mockConfig.showAnswerAfterSubmit : undefined,
        },
      });
      const globalRecords = mode === "sequential" ? (await getBankPracticeRecordsApi(bankId!)).items : [];
      return { practice, globalRecords };
    },
    onSuccess: ({ practice: data, globalRecords }: { practice: StartPracticeResponse; globalRecords: BankPracticeRecordDTO[] }) => {
      const globalAnswerRecords = toAnswerRecords(globalRecords);
      const initialIndex = getInitialQuestionIndex(data.questions, globalAnswerRecords, searchParams.get("questionId"));
      const initialQuestion = data.questions[initialIndex] ?? data.first_question;
      const initialRecord = initialQuestion ? globalAnswerRecords[initialQuestion.id] : undefined;

      setSessionId(data.session_id);
      setQuestions(data.questions);
      setCurrentQuestion(initialQuestion);
      setTotalQuestions(data.total_questions);
      setCurrentIndex(initialIndex);
      setAnswerStats(countAnswerRecords(globalAnswerRecords));
      setAnswerRecords(globalAnswerRecords);
      setOptimisticQuestionStats({});
      setAnswerInputResetKey((prev) => prev + 1);
      if (initialQuestion && initialRecord) {
        setLastResult({
          is_correct: initialRecord.isCorrect,
          correct_answer: initialQuestion.correct_answer,
          explanation: initialQuestion.explanation,
          next_question: data.questions[initialIndex + 1] ?? null,
          correct_count: countAnswerRecords(globalAnswerRecords).correct,
          total_count: data.total_questions,
        });
        setShowResult(true);
        setStatus("result");
      } else {
        setLastResult(null);
        setShowResult(false);
        setStatus("active");
      }
      setRemainingSeconds(mode === "mock" ? mockConfig.durationMinutes * 60 : null);
      pendingAnswersRef.current = [];
      questionStartTimeRef.current = Date.now();
    },
    onError: (err: Error) => {
      if (err.message.includes("NO_QUESTIONS") || err.message.includes("该题库暂无题目") || err.message.includes("该题库还没有题目")) {
        toast.error("该题库还没有题目，请先添加题目");
        navigate(`/banks/${bankId}`);
      } else {
        toast.error(`开始练习失败：${err.message}`);
        navigate(-1);
      }
    },
  });

  useEffect(() => {
    if (bankId && mode && status === "loading" && !startMutation.isPending && (mode !== "mock" || mockStarted)) {
      startMutation.mutate();
    }
  }, [bankId, mode, status, startMutation.isPending, mockStarted]);

  // ========== 2. 提交答案 ==========
  const flushPendingAnswers = useCallback(
    async (force = false) => {
      if (!sessionId) return;
      if (!force && pendingAnswersRef.current.length < ANSWER_SYNC_BATCH_SIZE) return;
      if (flushInFlightRef.current) {
        await flushInFlightRef.current;
        if (!force && pendingAnswersRef.current.length < ANSWER_SYNC_BATCH_SIZE) return;
      }

      const batch = [...pendingAnswersRef.current];
      if (batch.length === 0) return;

      const task = syncPracticeAnswersApi(sessionId, batch)
        .then(() => {
          pendingAnswersRef.current = pendingAnswersRef.current.filter(
            (pending) => !batch.some((item) => item.question_id === pending.question_id)
          );
          for (const item of batch) {
            queryClient.invalidateQueries({ queryKey: ["question-practice-stats", item.question_id] });
          }
        })
        .catch((err: Error) => {
          if (force) {
            throw err;
          }
        })
        .finally(() => {
          flushInFlightRef.current = null;
        });

      flushInFlightRef.current = task;
      await task;
    },
    [queryClient, sessionId]
  );

  const handleSubmitAnswer = useCallback(
    (answer: unknown) => {
      if (!currentQuestion) return;
      if (answerRecords[currentQuestion.id]) return;

      const normalizedAnswer = normalizeUserAnswer(currentQuestion.type, answer);
      if (normalizedAnswer === null) return;

      const timeSpent = Math.round((Date.now() - questionStartTimeRef.current) / 1000);
      const isCorrect = checkAnswerLocally(currentQuestion.type, currentQuestion.correct_answer, normalizedAnswer);
      const nextIndex = currentIndex + 1;
      const nextQuestion = questions[nextIndex] ?? null;
      const nextCorrectCount = answerStats.correct + (isCorrect ? 1 : 0);
      const nextWrongCount = answerStats.wrong + (isCorrect ? 0 : 1);

      pendingAnswersRef.current.push({
        question_id: currentQuestion.id,
        user_answer: normalizedAnswer,
        time_spent_seconds: timeSpent,
        answered_at: Date.now(),
      });

      const cachedStats = queryClient.getQueryData<QuestionPracticeStatsDTO>([
        "question-practice-stats",
        currentQuestion.id,
      ]);
      setOptimisticQuestionStats((prev) => ({
        ...prev,
        [currentQuestion.id]: incrementQuestionStats(prev[currentQuestion.id] ?? cachedStats, isCorrect),
      }));

      void flushPendingAnswers(false);

      setAnswerStats({
        correct: nextCorrectCount,
        wrong: nextWrongCount,
      });
      setAnswerRecords((prev) => ({
        ...prev,
        [currentQuestion.id]: {
          isCorrect,
          userAnswer: normalizedAnswer,
        },
      }));

      const result: SubmitAnswerResponse = {
        is_correct: isCorrect,
        correct_answer: currentQuestion.correct_answer,
        explanation: currentQuestion.explanation,
        next_question: nextQuestion,
        correct_count: nextCorrectCount,
        total_count: totalQuestions,
      };

      if (mode === "mock" && !mockConfig.showAnswerAfterSubmit && nextQuestion) {
        setCurrentQuestion(nextQuestion);
        setShowResult(false);
        setLastResult(null);
        setStatus("active");
        setCurrentIndex(nextIndex);
        questionStartTimeRef.current = Date.now();
        return;
      }

      setLastResult(result);
      setShowResult(true);
      setStatus("result");
    },
    [answerRecords, answerStats.correct, answerStats.wrong, currentIndex, currentQuestion, flushPendingAnswers, mode, mockConfig.showAnswerAfterSubmit, optimisticQuestionStats, queryClient, questions, totalQuestions]
  );

  const handleJumpToQuestion = useCallback(
    (index: number) => {
      const question = questions[index];
      if (!question) return;
      const record = answerRecords[question.id];
      setCurrentQuestion(question);
      setCurrentIndex(index);
      questionStartTimeRef.current = Date.now();

      if (record) {
        setLastResult({
          is_correct: record.isCorrect,
          correct_answer: question.correct_answer,
          explanation: question.explanation,
          next_question: questions[index + 1] ?? null,
          correct_count: answerStats.correct,
          total_count: totalQuestions,
        });
        setShowResult(true);
        setStatus("result");
      } else {
        setLastResult(null);
        setShowResult(false);
        setStatus("active");
      }
    },
    [answerRecords, answerStats.correct, questions, totalQuestions]
  );

  // ========== 3. 下一题 / 完成 ==========

  const handleNext = useCallback(() => {
    if (!lastResult) return;

    if (lastResult.next_question) {
      // 还有下一题
      const nextIndex = Math.min(currentIndex + 1, questions.length - 1);
      const nextQuestion = questions[nextIndex] ?? lastResult.next_question;
      const record = answerRecords[nextQuestion.id];

      setCurrentQuestion(nextQuestion);
      setCurrentIndex(nextIndex);
      questionStartTimeRef.current = Date.now();
      if (record) {
        setLastResult({
          is_correct: record.isCorrect,
          correct_answer: nextQuestion.correct_answer,
          explanation: nextQuestion.explanation,
          next_question: questions[nextIndex + 1] ?? null,
          correct_count: answerStats.correct,
          total_count: totalQuestions,
        });
        setShowResult(true);
        setStatus("result");
      } else {
        setShowResult(false);
        setLastResult(null);
        setStatus("active");
      }
    } else {
      // 全部完成 → 结算
      handleComplete();
    }
  }, [answerRecords, answerStats.correct, currentIndex, lastResult, questions, totalQuestions]);

  // ========== 4. 完成练习 ==========

  const completeMutation = useMutation({
    mutationFn: async (sid: string) => {
      await flushPendingAnswers(true);
      return completeSessionApi(sid);
    },
    onSuccess: (data) => {
      setScore(data);
      setStatus("completed");
    },
    onError: (err: Error) => {
      toast.error(`结算失败：${err.message}`);
      // 仍然显示结果页（使用当前累计数据）
      setStatus("completed");
    },
  });

  const handleComplete = useCallback(() => {
    if (!sessionId) return;
    completeMutation.mutate(sessionId);
  }, [sessionId, completeMutation]);

  const clearPracticeRecordsMutation = useMutation({
    mutationFn: async () => {
      if (!bankId) return;
      if (flushInFlightRef.current) {
        await flushInFlightRef.current.catch(() => undefined);
      }
      pendingAnswersRef.current = [];
      await clearBankPracticeRecordsApi(bankId);
    },
    onSuccess: () => {
      setAnswerRecords({});
      setAnswerStats({ correct: 0, wrong: 0 });
      setOptimisticQuestionStats({});
      setShowResult(false);
      setLastResult(null);
      setStatus("active");
      setScore(null);
      setAnswerInputResetKey((prev) => prev + 1);
      queryClient.invalidateQueries({ queryKey: ["bank", bankId, "stats"] });
      queryClient.invalidateQueries({ queryKey: ["question-practice-stats"] });
      toast.success("答题记录已清空");
    },
    onError: (err: Error) => {
      toast.error(`清空失败：${err.message}`);
    },
  });

  const handleLeavePractice = useCallback(async () => {
    if (isLeaving) return;

    setIsLeaving(true);
    try {
      await flushPendingAnswers(true);
      if (bankId) {
        queryClient.invalidateQueries({ queryKey: ["bank", bankId] });
        queryClient.invalidateQueries({ queryKey: ["bank", bankId, "stats"] });
      }
      navigate(-1);
    } catch (err) {
      const message = err instanceof Error ? err.message : "答题进度同步失败";
      toast.error(`返回前同步失败：${message}`);
      setIsLeaving(false);
    }
  }, [bankId, flushPendingAnswers, isLeaving, navigate, queryClient]);

  useEffect(() => {
    if (mode !== "mock" || remainingSeconds === null || status === "loading" || status === "completed") return;
    if (remainingSeconds <= 0) {
      handleComplete();
      return;
    }
    const timer = window.setTimeout(() => {
      setRemainingSeconds((prev) => (prev === null ? null : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [handleComplete, mode, remainingSeconds, status]);

  const updateSettings = useCallback((patch: Partial<PracticeSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      savePracticeSettings(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (status !== "result" || !lastResult?.is_correct || !lastResult.next_question || !settings.autoNextOnCorrect) return;
    const timer = window.setTimeout(() => {
      handleNext();
    }, 650);
    return () => window.clearTimeout(timer);
  }, [status, lastResult, settings.autoNextOnCorrect, handleNext]);

  useEffect(() => {
    if (status !== "result" || !lastResult) return;
    if (settings.answerSound) {
      playAnswerTone(lastResult.is_correct);
    }
    if (settings.readAnswer || settings.readExplanation) {
      const parts = [
        settings.readAnswer ? (lastResult.is_correct ? "回答正确" : `回答错误，正确答案是${formatSpokenAnswer(lastResult.correct_answer)}`) : "",
        settings.readExplanation && lastResult.explanation ? `解析：${lastResult.explanation}` : "",
      ].filter(Boolean);
      speakText(parts.join("。"));
    }
  }, [status, lastResult, settings.answerSound, settings.readAnswer, settings.readExplanation]);

  useEffect(() => {
    return () => {
      void flushPendingAnswers(true);
    };
  }, [flushPendingAnswers]);

  // ========== 渲染 ==========

  // Loading 状态
  if (mode === "mock" && !mockStarted) {
    return (
      <MockExamSetupView
        config={mockConfig}
        typeStats={typeStats}
        onChange={setMockConfig}
        onBack={() => navigate(-1)}
        onStart={() => {
          setMockStarted(true);
          setStatus("loading");
        }}
      />
    );
  }

  if (status === "loading" || startMutation.isPending) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        <p className="mt-4 text-gray-500">正在准备练习...</p>
      </div>
    );
  }

  // 完成状态 — 成绩页
  if (status === "completed") {
    const s = score ?? {
      score: lastResult
        ? Math.round((lastResult.correct_count / lastResult.total_count) * 100)
        : 0,
      correct_count: lastResult?.correct_count ?? 0,
      total_count: lastResult?.total_count ?? 0,
    };

    return (
      <PracticeResultView
        score={s}
        isMock={mode === "mock"}
        maxScore={getMockTotalScore(mockConfig)}
        passScore={mockConfig.passScore}
        onBack={() => navigate(`/banks/${bankId}`)}
        onRetry={() => {
          // 重新开始时刷新页面（重新调用 startPracticeApi）
          setStatus("loading");
          setCurrentQuestion(null);
          setQuestions([]);
          setShowResult(false);
          setLastResult(null);
          setScore(null);
          setCurrentIndex(0);
          setAnswerStats({ correct: 0, wrong: 0 });
          setAnswerRecords({});
          setOptimisticQuestionStats({});
          startMutation.mutate();
        }}
      />
    );
  }

  // 活跃状态 + 结果显示状态
  return (
    <PracticeActiveView
      status={status}
      currentQuestion={currentQuestion}
      currentIndex={currentIndex}
      totalQuestions={totalQuestions}
      showResult={showResult}
      lastResult={lastResult}
      onSubmitAnswer={handleSubmitAnswer}
      onNext={handleNext}
      isSubmitting={completeMutation.isPending}
      mode={mode ?? "sequential"}
      bankId={bankId ?? ""}
      correctCount={answerStats.correct}
      wrongCount={answerStats.wrong}
      onBack={() => void handleLeavePractice()}
      settings={settings}
      onUpdateSettings={updateSettings}
      questions={questions}
      answerRecords={answerRecords}
      onJumpToQuestion={handleJumpToQuestion}
      remainingSeconds={remainingSeconds}
      isMock={mode === "mock"}
      onComplete={handleComplete}
      randomOrder={mode === "mock" ? mockConfig.randomOrder : settings.randomOrder}
      historyStats={showResult ? currentQuestionStats : undefined}
      onClearPracticeRecords={mode === "sequential" ? () => clearPracticeRecordsMutation.mutate() : undefined}
      isClearingPracticeRecords={clearPracticeRecordsMutation.isPending}
      answerInputResetKey={answerInputResetKey}
      isLeaving={isLeaving}
    />
  );
}

function incrementQuestionStats(stats: QuestionPracticeStatsDTO | undefined, isCorrect: boolean): QuestionPracticeStatsDTO {
  const totalCount = (stats?.total_count ?? 0) + 1;
  const correctCount = (stats?.correct_count ?? 0) + (isCorrect ? 1 : 0);
  return {
    total_count: totalCount,
    correct_count: correctCount,
    accuracy_rate: totalCount > 0 ? Math.round((correctCount / totalCount) * 10000) / 100 : 0,
  };
}

function toAnswerRecords(records: BankPracticeRecordDTO[]): Record<string, AnswerRecord> {
  return Object.fromEntries(
    records.map((record) => [
      record.question_id,
      {
        isCorrect: record.is_correct,
        userAnswer: record.user_answer,
      },
    ])
  );
}

function countAnswerRecords(records: Record<string, AnswerRecord>): { correct: number; wrong: number } {
  return Object.values(records).reduce(
    (acc, record) => {
      if (record.isCorrect) acc.correct += 1;
      else acc.wrong += 1;
      return acc;
    },
    { correct: 0, wrong: 0 }
  );
}

function getInitialQuestionIndex(
  questions: QuestionDTO[],
  records: Record<string, AnswerRecord>,
  preferredQuestionId: string | null
): number {
  if (preferredQuestionId) {
    const preferredIndex = questions.findIndex((question) => question.id === preferredQuestionId);
    if (preferredIndex >= 0) return preferredIndex;
  }

  const firstUnansweredIndex = questions.findIndex((question) => !records[question.id]);
  return firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0;
}

function mergeQuestionStats(
  serverStats: QuestionPracticeStatsDTO | undefined,
  optimisticStats: QuestionPracticeStatsDTO | undefined
): QuestionPracticeStatsDTO | undefined {
  if (!serverStats) return optimisticStats;
  if (!optimisticStats) return serverStats;

  const totalCount = Math.max(serverStats.total_count, optimisticStats.total_count);
  const correctCount = Math.max(serverStats.correct_count, optimisticStats.correct_count);
  return {
    total_count: totalCount,
    correct_count: correctCount,
    accuracy_rate: totalCount > 0 ? Math.round((correctCount / totalCount) * 10000) / 100 : 0,
  };
}

// ========== 练习进行中视图 ==========

interface ActiveViewProps {
  status: PracticeStatus;
  currentQuestion: QuestionDTO | null;
  currentIndex: number;
  totalQuestions: number;
  showResult: boolean;
  lastResult: SubmitAnswerResponse | null;
  onSubmitAnswer: (answer: unknown) => void;
  onNext: () => void;
  isSubmitting: boolean;
  mode: string;
  bankId: string;
  correctCount: number;
  wrongCount: number;
  onBack: () => void;
  settings: PracticeSettings;
  onUpdateSettings: (patch: Partial<PracticeSettings>) => void;
  questions: QuestionDTO[];
  answerRecords: Record<string, AnswerRecord>;
  onJumpToQuestion: (index: number) => void;
  remainingSeconds: number | null;
  isMock: boolean;
  onComplete: () => void;
  randomOrder: boolean;
  historyStats?: QuestionPracticeStatsDTO;
  onClearPracticeRecords?: () => void;
  isClearingPracticeRecords?: boolean;
  answerInputResetKey: number;
  isLeaving: boolean;
}

function PracticeActiveView({
  currentQuestion,
  currentIndex,
  totalQuestions,
  showResult,
  lastResult,
  onSubmitAnswer,
  onNext,
  isSubmitting,
  mode,
  bankId,
  correctCount,
  wrongCount,
  onBack,
  settings,
  onUpdateSettings,
  questions,
  answerRecords,
  onJumpToQuestion,
  remainingSeconds,
  isMock,
  onComplete,
  randomOrder,
  historyStats,
  onClearPracticeRecords,
  isClearingPracticeRecords = false,
  answerInputResetKey,
  isLeaving,
}: ActiveViewProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [showAnswerCard, setShowAnswerCard] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <p className="text-gray-400">没有可练习的题目</p>
      </div>
    );
  }

  const modeLabel = getPracticeModeLabel(mode);
  const canSwipePrev = currentIndex > 0;
  const canSwipeNext = currentIndex < totalQuestions - 1;

  function handleTouchStart(event: React.TouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }

  function handleTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    if (isSubmitting || isLeaving || showAnswerCard || showSettings) return;

    const start = touchStartRef.current;
    const touch = event.changedTouches[0];
    touchStartRef.current = null;
    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const isHorizontalSwipe = Math.abs(deltaX) >= 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
    if (!isHorizontalSwipe) return;

    if (deltaX < 0 && canSwipeNext) {
      onJumpToQuestion(currentIndex + 1);
      return;
    }
    if (deltaX > 0 && canSwipePrev) {
      onJumpToQuestion(currentIndex - 1);
    }
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="sticky top-0 z-20 bg-blue-500 text-white shadow-sm">
        <div className="flex h-12 items-center justify-between px-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              disabled={isLeaving}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600/60"
              aria-label="返回"
            >
              <ChevronLeftIcon />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600/60"
              aria-label="练习设置"
            >
              <GearIcon />
            </button>
          </div>

          <div className="flex rounded-md bg-blue-600/50 p-0.5 text-sm font-medium">
            {["答题", "背题", "语音"].map((label, idx) => (
              <button
                key={label}
                className={`px-4 py-1 rounded ${idx === 0 ? "bg-white text-blue-600" : "text-white/85"}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-full bg-white/25 px-3 py-1">
            <span className="text-sm font-semibold">•••</span>
            <span className="h-5 w-px bg-white/25" />
            <span className="h-4 w-4 rounded-full border-2 border-white/90" />
          </div>
        </div>
      </header>

      <main className="px-4 pt-4" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="mb-3 flex items-center justify-between text-xs text-gray-400">
          <span>{modeLabel}练习</span>
          <span>{isMock && remainingSeconds !== null ? `剩余 ${formatDuration(remainingSeconds)}` : `${currentIndex + 1}/${totalQuestions}`}</span>
        </div>
        <QuestionRenderer
          key={`${currentQuestion.id}-${answerInputResetKey}`}
          question={currentQuestion}
          questionNumber={currentIndex + 1}
          onSubmit={showResult ? undefined : onSubmitAnswer}
          disabled={showResult || isSubmitting || isLeaving}
          showResult={showResult}
          isCorrect={lastResult?.is_correct}
          correctAnswer={lastResult?.correct_answer}
          explanation={lastResult?.explanation}
          fontSize={settings.fontSize}
          optionSpacing={settings.optionSpacing}
          shuffleOptions={randomOrder}
          historyStats={historyStats}
        />

        {showResult && currentQuestion && (
          <div className="pt-5">
            <PracticeResultActions
              questionId={currentQuestion.id}
              bankId={bankId}
              onNext={onNext}
              hasNext={!!lastResult?.next_question}
            />
          </div>
        )}
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-100 bg-white/95 backdrop-blur">
        <div className="grid h-16 grid-cols-4 items-center text-center text-xs text-gray-600">
          <button className="flex flex-col items-center gap-1">
            <StarIcon />
            <span>收藏</span>
          </button>
          <div className="flex flex-col items-center gap-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white text-xs">✓</span>
            <span>{correctCount}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs">×</span>
            <span>{wrongCount}</span>
          </div>
          <button onClick={() => setShowAnswerCard(true)} className="flex flex-col items-center gap-1">
            <GridIcon />
            <span>答题卡</span>
          </button>
        </div>
        {isMock && (
          <button
            type="button"
            onClick={onComplete}
            className="absolute bottom-20 right-4 rounded-full bg-[#7254ff] px-5 py-2 text-sm font-semibold text-white shadow-lg"
          >
            交卷
          </button>
        )}
      </nav>

      {/* 提交中遮罩 */}
      {isSubmitting && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/10 z-50">
          <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span className="text-gray-600">提交中...</span>
          </div>
        </div>
      )}

      {isLeaving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
          <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span className="text-gray-600">正在同步答题进度...</span>
          </div>
        </div>
      )}

      {showSettings && (
        <PracticeSettingsPanel
          settings={settings}
          onChange={onUpdateSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {showAnswerCard && (
        <AnswerCardPanel
          questions={questions}
          currentIndex={currentIndex}
          answerRecords={answerRecords}
          onClose={() => setShowAnswerCard(false)}
          onJump={(index) => {
            onJumpToQuestion(index);
            setShowAnswerCard(false);
          }}
          onClear={onClearPracticeRecords}
          isClearing={isClearingPracticeRecords}
        />
      )}
    </div>
  );
}

function PracticeSettingsPanel({
  settings,
  onChange,
  onClose,
}: {
  settings: PracticeSettings;
  onChange: (patch: Partial<PracticeSettings>) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-gray-100">
      <div className="min-h-screen bg-gray-100">
        <header className="sticky top-0 bg-white">
          <div className="flex h-12 items-center justify-between px-3">
            <button onClick={onClose} className="flex h-9 w-9 items-center justify-center text-gray-700" aria-label="返回">
              <ChevronLeftIcon />
            </button>
            <h2 className="text-base font-semibold text-gray-900">练习设置</h2>
            <div className="flex items-center gap-3 text-gray-800">
              <span className="text-lg leading-none">•••</span>
              <span className="h-5 w-5 rounded-full border-2 border-gray-800" />
            </div>
          </div>
        </header>

        <div className="bg-white">
          <SettingLine label="选择题的选项显示" value="正常显示" />
          <SettingSwitch
            label="显示上一题和下一题按钮"
            checked={settings.showPrevNext}
            onChange={(value) => onChange({ showPrevNext: value })}
          />
          <SettingSwitch
            label="答对自动下一题"
            checked={settings.autoNextOnCorrect}
            onChange={(value) => onChange({ autoNextOnCorrect: value })}
          />
          <SettingSwitch
            label="选项乱序"
            checked={settings.randomOrder}
            onChange={(value) => onChange({ randomOrder: value })}
          />
          <SettingSwitch
            label="答题音效"
            checked={settings.answerSound}
            onChange={(value) => onChange({ answerSound: value })}
          />
          <SettingSwitch
            label="朗读解析"
            checked={settings.readExplanation}
            onChange={(value) => onChange({ readExplanation: value })}
          />
          <SettingSwitch
            label="朗读答案"
            checked={settings.readAnswer}
            onChange={(value) => onChange({ readAnswer: value })}
          />
          <SettingLine
            label="随机练习每组题数"
            value={String(settings.randomQuestionCount)}
            onClick={() => {
              const next = window.prompt("随机练习每组题数", String(settings.randomQuestionCount));
              if (!next) return;
              const count = Math.max(1, Math.min(500, Number(next)));
              if (!Number.isNaN(count)) onChange({ randomQuestionCount: count });
            }}
          />
          <div className="flex min-h-[54px] items-center justify-between border-t border-gray-100 px-4">
            <div>
              <p className="text-[15px] text-gray-900">连续做对几次移出我的错题</p>
              <p className="mt-0.5 text-xs text-gray-400">0代表永不移出错题</p>
            </div>
            <Stepper
              value={settings.wrongRepeatThreshold}
              min={0}
              max={20}
              onChange={(value) => onChange({ wrongRepeatThreshold: value })}
            />
          </div>
          <div className="border-t border-gray-100 px-4 py-3">
            <p className="mb-3 text-[15px] text-gray-900">字体大小</p>
            <div className="flex items-end justify-between text-gray-900">
              {[
                ["small", "小号"],
                ["standard", "标准"],
                ["large", "加大"],
                ["xlarge", "很大"],
                ["xxlarge", "特大"],
                ["max", "最大"],
              ].map(([value, label], idx) => (
                <button
                  key={value}
                  onClick={() => onChange({ fontSize: value as FontSizeSetting })}
                  className={`font-semibold ${settings.fontSize === value ? "text-blue-600" : ""}`}
                  style={{ fontSize: `${13 + idx * 2}px` }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex min-h-[58px] items-center justify-between border-t border-gray-100 px-4">
            <p className="text-[15px] text-gray-900">选项间距</p>
            <div className="grid grid-cols-3 overflow-hidden rounded-lg border border-blue-500 text-sm">
              {[
                ["standard", "标准"],
                ["wide", "较大"],
                ["xwide", "增大"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => onChange({ optionSpacing: value as OptionSpacingSetting })}
                  className={`px-4 py-1.5 ${
                    settings.optionSpacing === value ? "bg-blue-500 text-white" : "bg-white text-blue-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnswerCardPanel({
  questions,
  currentIndex,
  answerRecords,
  onClose,
  onJump,
  onClear,
  isClearing = false,
}: {
  questions: QuestionDTO[];
  currentIndex: number;
  answerRecords: Record<string, AnswerRecord>;
  onClose: () => void;
  onJump: (index: number) => void;
  onClear?: () => void;
  isClearing?: boolean;
}) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const answeredCount = questions.filter((q) => answerRecords[q.id]).length;
  const wrongCount = questions.filter((q) => answerRecords[q.id] && !answerRecords[q.id].isCorrect).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/30">
      <div className="w-full rounded-t-2xl bg-white p-4 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">答题卡</h2>
            <p className="mt-1 text-xs text-gray-400">
              已答 {answeredCount} / {questions.length}，错 {wrongCount}
            </p>
          </div>
          <button onClick={onClose} className="rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-500">
            关闭
          </button>
        </div>

        <div className="grid max-h-[55vh] grid-cols-5 gap-3 overflow-y-auto pb-2">
          {questions.map((question, index) => {
            const record = answerRecords[question.id];
            const isCurrent = index === currentIndex;
            const className = record
              ? record.isCorrect
                ? "border-green-500 bg-green-500 text-white"
                : "border-red-500 bg-red-500 text-white"
              : isCurrent
              ? "border-blue-500 bg-blue-50 text-blue-600"
              : "border-gray-200 bg-white text-gray-500";

            return (
              <button
                key={question.id}
                type="button"
                onClick={() => onJump(index)}
                className={`h-11 rounded-lg border text-sm font-semibold ${className}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-500">
          <LegendDot className="bg-white border-gray-200" label="未答" />
          <LegendDot className="bg-green-500 border-green-500" label="正确" />
          <LegendDot className="bg-red-500 border-red-500" label="错误" />
        </div>

        {onClear && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              disabled={isClearing || answeredCount === 0}
              onClick={() => setShowClearConfirm(true)}
              className="h-11 rounded-full border border-[#7057ff] text-sm font-semibold text-[#7057ff] disabled:opacity-40"
            >
              {isClearing ? "清空中..." : "清空答题记录"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-full border border-[#7057ff] text-sm font-semibold text-[#7057ff]"
            >
              查看练习结果
            </button>
          </div>
        )}
      </div>

      {showClearConfirm && onClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-card-hover">
            <h3 className="mb-2 text-base font-bold text-gray-900">确认清空答题记录</h3>
            <p className="mb-4 text-sm leading-6 text-gray-500">
              将清空本题库的完成率、答对和答错记录，错题和笔记不会清空。
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-gray-700"
              >
                取消
              </button>
              <button
                type="button"
                disabled={isClearing}
                onClick={() => onClear()}
                className="flex-1 rounded-xl bg-[#7057ff] py-2.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {isClearing ? "清空中..." : "确认清空"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      <span className={`h-3 w-3 rounded-full border ${className}`} />
      {label}
    </div>
  );
}

function MockExamSetupView({
  config,
  typeStats,
  onChange,
  onBack,
  onStart,
}: {
  config: MockExamConfig;
  typeStats?: {
    single_choice: number;
    multiple_choice: number;
    true_false: number;
    fill_blank: number;
    essay: number;
  };
  onChange: (config: MockExamConfig) => void;
  onBack: () => void;
  onStart: () => void;
}) {
  const totalCount = config.single_choice.count + config.multiple_choice.count + config.true_false.count;
  const totalScore =
    config.single_choice.count * config.single_choice.score +
    config.multiple_choice.count * config.multiple_choice.score +
    config.true_false.count * config.true_false.score;

  const updateType = (type: keyof Pick<MockExamConfig, "single_choice" | "multiple_choice" | "true_false" | "fill_blank">, patch: Partial<{ count: number; score: number }>) => {
    onChange({ ...config, [type]: { ...config[type], ...patch } });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-36">
      <header className="sticky top-0 z-20 bg-white">
        <div className="relative flex h-16 items-center justify-center border-b border-gray-100 px-4">
          <button onClick={onBack} className="absolute left-3 flex h-10 w-10 items-center justify-center text-gray-900" aria-label="返回">
            <ChevronLeftIcon />
          </button>
          <h1 className="text-[20px] font-semibold text-gray-950">模拟考试</h1>
          <div className="absolute right-3 flex h-10 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-900">
            <span className="text-lg leading-none">•••</span>
            <span className="mx-3 h-5 w-px bg-gray-200" />
            <span className="h-5 w-5 rounded-full border-[5px] border-gray-900" />
          </div>
        </div>
      </header>

      <section className="px-4 pt-4">
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">题型配置</h2>
              <p className="mt-1 text-xs text-gray-400">按题型设置题数和每题分值</p>
            </div>
            <div className="rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-[#6f55ff]">
              共 {totalCount} 题
            </div>
          </div>
          <MockTypeRow label="单选题" available={typeStats?.single_choice ?? 0} value={config.single_choice} onChange={(patch) => updateType("single_choice", patch)} />
          <MockTypeRow label="多选题" available={typeStats?.multiple_choice ?? 0} value={config.multiple_choice} onChange={(patch) => updateType("multiple_choice", patch)} />
          <MockTypeRow label="判断题" available={typeStats?.true_false ?? 0} value={config.true_false} onChange={(patch) => updateType("true_false", patch)} />
          <MockTypeRow label="填空题" available={typeStats?.fill_blank ?? 0} value={config.fill_blank} onChange={(patch) => updateType("fill_blank", patch)} />
          <div className="grid grid-cols-2 gap-3 border-t border-gray-100 px-4 py-4">
            <div className="rounded-xl bg-gray-50 px-3 py-3">
              <p className="text-xs text-gray-400">总题数</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {totalCount}
                <span className="ml-1 text-sm font-medium text-[#6f55ff]">题</span>
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-3 py-3">
              <p className="text-xs text-gray-400">总分值</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {totalScore.toFixed(2)}
                <span className="ml-1 text-sm font-medium text-[#6f55ff]">分</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pt-4">
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <SettingLine label="考试时长" value={`${config.durationMinutes}分钟`} onClick={() => {
            const next = window.prompt("考试时长（分钟）", String(config.durationMinutes));
            if (!next) return;
            const value = Math.max(1, Math.min(300, Number(next)));
            if (!Number.isNaN(value)) onChange({ ...config, durationMinutes: value });
          }} />
          <div className="flex min-h-[58px] items-center justify-between border-t border-gray-100 px-4">
            <span className="text-[15px] text-gray-900">及格分</span>
            <div className="flex min-w-0 items-center gap-2">
              <input
                type="number"
                value={config.passScore}
                onChange={(e) => onChange({ ...config, passScore: Number(e.target.value) || 0 })}
                className="w-24 rounded-xl bg-gray-100 px-3 py-2 text-right text-base font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-[#6f55ff]/20"
              />
              <span className="text-sm text-gray-500">分</span>
            </div>
          </div>
          <SettingSwitch label="答题后显示答案" checked={config.showAnswerAfterSubmit} onChange={(value) => onChange({ ...config, showAnswerAfterSubmit: value })} />
          <SettingLine label="多选题得分模式" value="全部选对，才算得分" />
          <SettingSwitch label="选项乱序" checked={config.randomOrder} onChange={(value) => onChange({ ...config, randomOrder: value })} />
          <SettingSwitch label="错题优先" checked={config.wrongFirst} onChange={(value) => onChange({ ...config, wrongFirst: value })} />
          <SettingSwitch label="未做题优先" checked={config.unansweredFirst} onChange={(value) => onChange({ ...config, unansweredFirst: value })} />
        </div>
      </section>

      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-100 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 backdrop-blur">
        <div className="mx-auto flex w-full max-w-md items-center gap-3">
        <button className="flex h-14 w-20 flex-col items-center justify-center rounded-2xl bg-gray-50 text-xs text-gray-600">
          <span className="text-2xl leading-none">↺</span>
          历史记录
        </button>
        <button
          onClick={onStart}
          disabled={totalCount === 0}
          className="flex-1 rounded-2xl bg-[#7254ff] py-4 text-lg font-semibold text-white shadow-[0_12px_30px_rgba(114,84,255,0.28)] disabled:bg-gray-300 disabled:shadow-none"
        >
          开始考试
        </button>
        </div>
      </footer>
    </div>
  );
}

function MockTypeRow({
  label,
  available,
  value,
  onChange,
}: {
  label: string;
  available: number;
  value: { count: number; score: number };
  onChange: (patch: Partial<{ count: number; score: number }>) => void;
}) {
  return (
    <div className="border-t border-gray-100 px-4 py-4 first:border-t-0">
      <div className="mb-3 min-w-0">
        <p className="text-base font-semibold text-gray-900">{label}</p>
        <p className="mt-1 text-xs text-gray-400">题库可用 {available} 题</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="min-w-0 rounded-xl bg-gray-50 px-3 py-3">
          <span className="mb-1 block text-xs text-gray-400">题数</span>
          <input
            type="number"
            value={value.count}
            onChange={(e) => onChange({ count: Math.min(available, Math.max(0, Number(e.target.value) || 0)) })}
            className="w-full min-w-0 bg-transparent text-lg font-semibold text-gray-900 outline-none"
          />
        </label>
        <label className="min-w-0 rounded-xl bg-gray-50 px-3 py-3">
          <span className="mb-1 block text-xs text-gray-400">每题分值</span>
          <input
            type="number"
            step="0.5"
            value={value.score}
            onChange={(e) => onChange({ score: Math.max(0, Number(e.target.value) || 0) })}
            className="w-full min-w-0 bg-transparent text-lg font-semibold text-gray-900 outline-none"
          />
        </label>
      </div>
    </div>
  );
}

function SettingLine({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[56px] w-full items-center justify-between border-t border-gray-100 px-4 text-left"
    >
      <span className="text-[15px] text-gray-900">{label}</span>
      <span className="ml-4 flex min-w-0 items-center gap-1 text-sm text-gray-400">
        <span className="truncate">{value}</span>
        <span className="text-gray-300">›</span>
      </span>
    </button>
  );
}

function SettingSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-h-[52px] items-center justify-between border-t border-gray-100 px-4">
      <span className="text-[15px] text-gray-900">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 rounded-full transition-colors ${checked ? "bg-blue-500" : "bg-gray-200"}`}
        aria-pressed={checked}
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function Stepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex items-center overflow-hidden rounded bg-gray-50 text-sm text-gray-700">
      <button className="h-8 w-8 text-lg" onClick={() => onChange(Math.max(min, value - 1))}>
        -
      </button>
      <span className="w-9 text-center font-semibold">{value}</span>
      <button className="h-8 w-8 text-lg" onClick={() => onChange(Math.min(max, value + 1))}>
        +
      </button>
    </div>
  );
}

// ========== 练习结果操作栏 ==========

function PracticeResultActions({
  questionId,
  bankId,
  onNext,
  hasNext,
}: {
  questionId: string;
  bankId: string;
  onNext: () => void;
  hasNext: boolean;
}) {
  const queryClient = useQueryClient();

  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteContent, setNoteContent] = useState("");

  const favoriteMut = useMutation({
    mutationFn: () => addFavoriteApi(questionId),
    onSuccess: () => {
      toast.success("已收藏");
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
    },
    onError: () => toast.error("收藏失败"),
  });

  const noteMut = useMutation({
    mutationFn: () => createNoteApi({ bank_id: bankId, question_id: questionId, content: noteContent }),
    onSuccess: () => {
      toast.success("笔记已保存");
      setShowNoteInput(false);
      setNoteContent("");
      queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
    onError: () => toast.error("保存笔记失败"),
  });

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <button
          disabled={favoriteMut.isPending}
          onClick={() => favoriteMut.mutate()}
          className="flex-1 py-2.5 rounded-full border border-gray-200 text-gray-600 text-sm font-medium active:bg-gray-50 disabled:opacity-40"
        >
          收藏
        </button>
        <button
          onClick={() => setShowNoteInput(!showNoteInput)}
          className="flex-1 py-2.5 rounded-full border border-blue-200 text-blue-600 text-sm font-medium active:bg-blue-50"
        >
          添加笔记
        </button>
      </div>

      {showNoteInput && (
        <div className="bg-white rounded-xl border p-3 space-y-2">
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="写下你的笔记..."
            className="w-full p-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            rows={3}
          />
          <div className="flex gap-2">
            <button
              onClick={() => setShowNoteInput(false)}
              className="flex-1 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-sm"
            >
              取消
            </button>
            <button
              disabled={!noteContent.trim() || noteMut.isPending}
              onClick={() => noteMut.mutate()}
              className="flex-1 py-1.5 rounded-lg bg-blue-500 text-white text-sm disabled:opacity-40"
            >
              {noteMut.isPending ? "保存中..." : "保存"}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={onNext}
        className="w-full py-3 bg-blue-500 text-white font-semibold rounded-full shadow-sm active:bg-blue-600"
      >
        {hasNext ? "下一题" : "查看成绩"}
      </button>
    </div>
  );
}

// ========== 成绩页视图 ==========

interface ResultViewProps {
  score: { score: number; correct_count: number; total_count: number };
  isMock?: boolean;
  maxScore?: number;
  passScore?: number;
  onBack: () => void;
  onRetry: () => void;
}

function PracticeResultView({ score, isMock = false, maxScore = 100, passScore = 60, onBack, onRetry }: ResultViewProps) {
  const { score: scoreNum, correct_count, total_count } = score;
  const ringScore = isMock && maxScore > 0 ? Math.min(100, Math.round((scoreNum / maxScore) * 100)) : scoreNum;

  // 评级
  let rating = "继续加油";
  let ratingColor = "text-gray-500";
  if (isMock ? scoreNum >= passScore : scoreNum >= 90) {
    rating = "优秀！";
    ratingColor = "text-green-500";
  } else if (!isMock && scoreNum >= 70) {
    rating = "良好";
    ratingColor = "text-blue-500";
  } else if (!isMock && scoreNum >= 60) {
    rating = "及格";
    ratingColor = "text-yellow-500";
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-card p-6 text-center">
        {/* 成绩圆环 */}
        <div className="relative w-32 h-32 mx-auto mb-6">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="8"
            />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke={(isMock ? scoreNum >= passScore : scoreNum >= 60) ? "#10B981" : "#EF4444"}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 52}`}
              strokeDashoffset={`${2 * Math.PI * 52 * (1 - ringScore / 100)}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${(isMock ? scoreNum >= passScore : scoreNum >= 60) ? "text-green-500" : "text-red-500"}`}>
              {scoreNum}
            </span>
            <span className="text-xs text-gray-400">{isMock ? `/${maxScore}分` : "分"}</span>
          </div>
        </div>

        <p className={`text-lg font-semibold mb-1 ${ratingColor}`}>{rating}</p>
        <p className="text-sm text-gray-500 mb-6">
          答对 {correct_count} 题 / 共 {total_count} 题{isMock ? `，及格 ${passScore} 分` : ""}
        </p>

        {/* 操作按钮 */}
        <div className="space-y-3">
          <button
            onClick={onRetry}
            className="w-full py-3 bg-primary text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors"
          >
            再来一次
          </button>
          <button
            onClick={onBack}
            className="w-full py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            返回题库
          </button>
        </div>
      </div>
    </div>
  );
}

function normalizeUserAnswer(
  type: QuestionDTO["type"],
  answer: unknown
): string | string[] | boolean | null {
  if (type === "multiple_choice") {
    if (!Array.isArray(answer)) return null;
    return [...answer].map(String).sort();
  }

  if (type === "fill_blank") {
    if (!Array.isArray(answer)) return null;
    return answer.map((item) => String(item).trim());
  }

  if (type === "essay") {
    return typeof answer === "string" ? answer.trim() : null;
  }

  if (type === "true_false") {
    return typeof answer === "boolean" ? answer : null;
  }

  if (type === "single_choice") {
    return typeof answer === "string" ? answer : null;
  }

  return null;
}

function checkAnswerLocally(type: QuestionDTO["type"], correctAnswerJson: string, userAnswer: string | string[] | boolean): boolean {
  try {
    const correct = JSON.parse(correctAnswerJson);

    if (type === "single_choice" || type === "true_false") {
      return userAnswer === correct;
    }

    if (type === "multiple_choice") {
      const userArr = Array.isArray(userAnswer) ? [...userAnswer].sort() : [];
      const correctArr = Array.isArray(correct) ? [...correct].map(String).sort() : [];
      return userArr.length === correctArr.length && userArr.every((value, index) => value === correctArr[index]);
    }

    if (type === "fill_blank") {
      const userArr = Array.isArray(userAnswer) ? userAnswer : [String(userAnswer)];
      const correctArr = Array.isArray(correct) ? correct.map(String) : [String(correct)];
      return (
        userArr.length === correctArr.length &&
        userArr.every((value, index) => value.trim().toLowerCase() === correctArr[index].trim().toLowerCase())
      );
    }

    if (type === "essay") {
      return normalizeEssayAnswer(userAnswer) === normalizeEssayAnswer(correct);
    }

    return false;
  } catch {
    return false;
  }
}

function normalizeEssayAnswer(value: unknown): string {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

function playAnswerTone(isCorrect: boolean) {
  try {
    const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = new AudioContextCtor();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = isCorrect ? 720 : 220;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.12);
  } catch {
    // Sound is optional.
  }
}

function speakText(text: string) {
  if (!text.trim() || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "zh-CN";
  utterance.rate = 1;
  window.speechSynthesis.speak(utterance);
}

function formatSpokenAnswer(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.join("、");
    if (typeof parsed === "boolean") return parsed ? "正确" : "错误";
    return String(parsed);
  } catch {
    return raw;
  }
}

function getPracticeModeLabel(mode: string): string {
  const map: Record<string, string> = {
    sequential: "顺序",
    random: "随机",
    type: "题型",
    wrong: "错题",
    favorite: "收藏",
    mock: "模拟考试",
  };
  return map[mode] ?? mode;
}

function getMockTotalScore(config: MockExamConfig): number {
  return Math.round((
    config.single_choice.count * config.single_choice.score +
    config.multiple_choice.count * config.multiple_choice.score +
    config.true_false.count * config.true_false.score +
    config.fill_blank.count * config.fill_blank.score
  ) * 100) / 100;
}

function buildMockTypeCounts(config: MockExamConfig): Record<string, number> {
  return {
    single_choice: config.single_choice.count,
    multiple_choice: config.multiple_choice.count,
    true_false: config.true_false.count,
    fill_blank: config.fill_blank.count,
  };
}

function buildMockScoreByType(config: MockExamConfig): Record<string, number> {
  return {
    single_choice: config.single_choice.score,
    multiple_choice: config.multiple_choice.score,
    true_false: config.true_false.score,
    fill_blank: config.fill_blank.score,
  };
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function ChevronLeftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 1 1.5h.1a1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.5 1z" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3l-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3z" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
    </svg>
  );
}
